import React from 'react';
import { ArrowRight, Users, Home, Car, Heart, CheckCircle, Clock, MapPin, Phone, Mail } from 'lucide-react';
import { services, organizationInfo } from '../mockData';

const ProgramsPage = () => {
  const serviceIcons = {
    "Peer Recovery Coaching": Users,
    "Recovery Housing Support": Home,
    "Transportation Services": Car,
    "Community Programming": Heart
  };

  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="py-16 bg-gray-50">
        <div className="container text-center">
          <h1 className="heading-1 mb-6">Our Programs</h1>
          <p className="body-large max-w-3xl mx-auto">
            We're developing comprehensive, evidence-based services to support
            individuals at every stage of their recovery journey. Here's a look at the peer-driven,
            trauma-informed programs we're bringing to Manistee and Mason Counties. You can also
            visit our <span className="font-semibold">Resources</span> page for help available right now.
          </p>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-16">
        <div className="container">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = serviceIcons[service.title] || Users;
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="p-4 rounded-full bg-green-100">
                        <IconComponent className="h-8 w-8 text-green-600" />
                      </div>
                      <h2 className="heading-2">{service.title}</h2>
                    </div>
                    
                    <p className="body-large mb-6">{service.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="heading-3 mb-4">Program Features:</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="body-medium">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h4 className="font-semibold mb-2" style={{color: 'var(--text-primary)'}}>Who Can Benefit:</h4>
                      <p className="body-small mb-3">
                        {service.id === 1 && "Individuals seeking one-on-one support from someone who understands their journey."}
                        {service.id === 2 && "People transitioning from treatment facilities or those experiencing housing instability."}
                        {service.id === 3 && "Clients who lack reliable transportation to access recovery services and support."}
                        {service.id === 4 && "Individuals looking to build recovery community connections and social support."}
                      </p>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-green-600" />
                          <span className="body-small">Email Support Available</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl">
                      <div className="aspect-square max-w-md mx-auto bg-white rounded-xl shadow-sm flex items-center justify-center">
                        <IconComponent className="h-24 w-24 text-green-600" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Approach</h2>
            <p className="body-large max-w-2xl mx-auto">
              We believe recovery is not a one-size-fits-all process. Our trauma-informed, 
              strength-based approach is tailored to each individual's unique needs and circumstances.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="heading-3 mb-3">Assessment & Planning</h3>
              <p className="body-medium">
                We begin with a comprehensive assessment to understand your unique situation, 
                goals, and barriers to recovery.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="heading-3 mb-3">Personalized Support</h3>
              <p className="body-medium">
                Your peer recovery specialist works with you to develop and implement 
                a personalized recovery plan.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="heading-3 mb-3">Ongoing Partnership</h3>
              <p className="body-medium">
                We maintain ongoing support and adjust services as your needs evolve 
                throughout your recovery journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility & Access */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Getting Started</h2>
              <p className="body-large">
                Our services are designed for individuals and families affected by
                substance use disorders. We're building accessible pathways to care — reach out
                today and we'll help connect you with support.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="heading-3 mb-4">Who We'll Serve</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="body-medium">18+ years of age</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="body-medium">History of substance use disorder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="body-medium">Commitment to recovery goals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="body-medium">Willingness to participate in services</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-8 rounded-lg">
                <h3 className="heading-3 mb-4">How to Reach Us Now</h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg">
                    <p className="body-small font-medium mb-2">For Questions & Support:</p>
                    <p className="caption">
                      We're not enrolling clients yet, but we're here to help. Email us at info@sagedrecovery.org,
                      or visit our Resources page for support available right now.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-green-50">
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Ready to Get Started?</h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Take the first step toward recovery today. Our compassionate team is here to support you 
            on your journey to lasting wellness.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`mailto:${organizationInfo.email}`} className="btn-primary flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Us: {organizationInfo.email}
            </a>
            <a href="/contact" className="btn-secondary flex items-center gap-2">
              Contact Form
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramsPage;